import React from "react";
import { ErrorIcon, GoAway } from "../Icons/Interface";
import { SnackbarProps } from "../../../DataModels/DataModels";
import SupportEmailAddressLink from "../Output/SupportEmailAddressLink";

/**
 * Simple close button for snackbars. Uses the same GoAway icon but does NOT
 * register in the SnackbarCloseButton LIFO stack, so snackbars never block
 * overlay close controls (Account Tray, Modals, etc.).
 */
const SnackbarCloseButton: React.FC<{
	className?: string;
	onClick?: () => void;
}> = ({
	onClick,
	className = "stroke-primary fill-primary hover:stroke-primary-inactive hover:fill-primary-inactive"
}) => (
	<div onClick={onClick} className={`cursor-pointer ${className}`}>
		{GoAway()}
	</div>
);

const getThemeClasses = (theme: "light" | "dark") => {
	if (theme === "light") {
		return {
			container: "bg-primary-active text-primary-inactive",
			text: "text-primary-inactive",
			icon: "fill-primary-inactive stroke-primary-inactive"
		};
	}
	return {
		container:
			"bg-primary-background text-primary-active border-primary-active",
		text: "text-primary-active",
		icon: "fill-primary-active stroke-primary-active"
	};
};

export const NotifyResumeUploaded: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Success! Your Resume has been uploaded.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				Use the "Track" button to start tracking your jobs.
			</p>
		</div>
	);
};

export const NotifyJobAdded: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Success! Your application has been added.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyJobUpdated: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Success! Your application has been updated.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};
export const NotifyQuestionAnswered: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Thank you for answering the questions.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				We are now reprocessing your resume and cover letter. This may take a
				few moments.
			</p>
		</div>
	);
};

export const NotifyLoggedOut: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					You have successfully logged out.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyResumeDone: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Your resume is done.
				</h1>
				{/* TODO: link to resume. How dows that work - is this a link to the Account page? Or to the JobResume page?  */}
			</div>
		</div>
	);
};

export const NotifyInternetDropped: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<div className={`mr-2 ${themeClasses.icon}`}>
					{ErrorIcon && ErrorIcon(24)}
				</div>
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Internet dropped. Please reconnect.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyNetworkError: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex items-center">
				<div className={`mr-2 ${themeClasses.icon}`}>
					{ErrorIcon && ErrorIcon(24)}
				</div>
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Network Error. Please try again.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyIncompleteJobApplication: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<div className={`mr-2 ${themeClasses.icon}`}>
					{ErrorIcon && ErrorIcon(24)}
				</div>
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Please fill in all required fields.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				We don't accept blank job applications. Please enter more information.
			</p>
		</div>
	);
};

export const NotifyLoginError: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<div className={`mr-2 ${themeClasses.icon}`}>
					{ErrorIcon && ErrorIcon(24)}
				</div>
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Something went wrong!
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				Please try again. Contact <SupportEmailAddressLink /> if the problem
				persists.
			</p>
		</div>
	);
};

export const NotifyAccountMismatch: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Account Mismatch
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				The account you selected does not match this subscription.
			</p>
		</div>
	);
};

export const NotifyAccountConflict: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Account Conflict
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				The account you selected already has an active subscription.
			</p>
		</div>
	);
};

export const NotifySubscriptionCancelled: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<div className={`mr-2 ${themeClasses.icon}`}>
					{ErrorIcon && ErrorIcon(24)}
				</div>
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Your subscription has been cancelled.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				Your premium features will remain active until the end of your billing
				period.
			</p>
		</div>
	);
};

export const NotifyPlanKept: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Great — your plan is safe!
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifySubscriptionCancelError: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<div className={`mr-2 ${themeClasses.icon}`}>
					{ErrorIcon && ErrorIcon(24)}
				</div>
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Something went wrong!
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				We couldn't cancel your subscription. Please try again or contact
				support.
			</p>
		</div>
	);
};

export const NotifyLinkCopied: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Link copied to clipboard!
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyContactAdded: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Contact added to job history.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyLetterSaved: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Letter saved to job history.
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyNoteSaved: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>Note saved.</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyNoteDeleted: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-full border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>Note deleted.</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
		</div>
	);
};

export const NotifyResumeRequired: React.FC<SnackbarProps> = ({
	theme = "dark",
	onGoAway
}) => {
	const themeClasses = getThemeClasses(theme);

	return (
		<div
			className={`${themeClasses.container} rounded-xl border p-4 shadow-lg`}
		>
			<div className="flex flex-row items-center justify-between">
				<div className={`mr-2 ${themeClasses.icon}`}>
					{ErrorIcon && ErrorIcon(24)}
				</div>
				<h1 className={`mb-0 ${themeClasses.text} font-bold`}>
					Resume Required
				</h1>
				{onGoAway && (
					<SnackbarCloseButton
						className={`${themeClasses.icon}`}
						onClick={onGoAway}
					/>
				)}
			</div>
			<p className={`${themeClasses.text} text-weight-100`}>
				Please upload your resume before downloading a cover letter. Your resume
				provides the contact details used on the letterhead.
			</p>
		</div>
	);
};

// Registry of available snackbar components
export const SNACKBAR_COMPONENTS = {
	NotifyResumeUploaded,
	NotifyQuestionAnswered,
	NotifyLoggedOut,
	NotifyResumeDone,
	NotifyInternetDropped,
	NotifyNetworkError,
	NotifyJobAdded,
	NotifyJobUpdated,
	NotifyIncompleteJobApplication,
	NotifyLoginError,
	NotifyAccountMismatch,
	NotifyAccountConflict,
	NotifySubscriptionCancelled,
	NotifySubscriptionCancelError,
	NotifyPlanKept,
	NotifyResumeRequired,
	NotifyContactAdded,
	NotifyLetterSaved,
	NotifyNoteSaved,
	NotifyNoteDeleted,
	NotifyLinkCopied
} as const;

export type SnackbarType = keyof typeof SNACKBAR_COMPONENTS;
